/**
 * 🎖️ ENCRYPTION MANAGER
 * High-level service that manages all encryption operations
 * Integrates e2eeService and encryptionApiService
 */

import e2eeService from "./e2eeService";
import encryptionApiService from "./encryptionApiService";
import { toast } from "react-toastify";

class EncryptionManager {
  constructor() {
    this.isE2EEEnabled = false;
    this.userPassword = null; // Store in memory during session
    this.publicKeysCache = new Map(); // Cache fetched public keys
  }

  /**
   * Initialize encryption system
   * @param {boolean} enableE2EE - Whether to enable E2EE
   */
  async initialize(enableE2EE = false) {
    try {
// Initialize E2EE service
      e2eeService.initialize();

      this.isE2EEEnabled = enableE2EE;

      // Check if user has keys
      const hasKeys = e2eeService.hasKeys();

      if (enableE2EE && !hasKeys) {
return {
          initialized: true,
          hasKeys: false,
          needsSetup: true,
        };
      }
return {
        initialized: true,
        hasKeys,
        needsSetup: false,
      };
    } catch (error) {
throw error;
    }
  }

  /**
   * Setup E2EE for new user
   * @param {string} password - User's password
   */
  async setupE2EE(password) {
    try {
// 1. Generate key pair
      const { publicKey, privateKey } = await e2eeService.generateKeyPair();

      // 2. Encrypt and store private key
      await e2eeService.storePrivateKey(privateKey, password);

      // 3. Store public key locally
      e2eeService.storePublicKey(publicKey);

      // 4. Upload public key to backend
      await encryptionApiService.uploadPublicKey(publicKey);

      // 5. Store password in memory
      this.userPassword = password;
      this.isE2EEEnabled = true;
toast.success("🔐 End-to-end encryption enabled!");

      return { success: true, publicKey };
    } catch (error) {
toast.error("Failed to setup encryption");
      throw error;
    }
  }

  /**
   * Load encryption keys (on login or page refresh)
   * @param {string} password - User's password
   */
  async loadKeys(password) {
    try {
// Load and decrypt private key
      await e2eeService.loadPrivateKey(password);

      // Store password in memory
      this.userPassword = password;
return { success: true };
    } catch (error) {
throw error;
    }
  }

  /**
   * Get recipient's public key (with caching)
   * @param {string} userId - Recipient user ID
   * @returns {Promise<string>} Public key (base64)
   */
  async getRecipientPublicKey(userId) {
    try {
      // Check cache first
      if (this.publicKeysCache.has(userId)) {
        return this.publicKeysCache.get(userId);
      }

      // Fetch from backend
      const publicKey = await encryptionApiService.getPublicKey(userId);

      // Cache it
      this.publicKeysCache.set(userId, publicKey);

      return publicKey;
    } catch (error) {
throw error;
    }
  }

  /**
   * Batch fetch multiple public keys
   * @param {string[]} userIds - Array of user IDs
   * @returns {Promise<Object>} Map of userId -> publicKey
   */
  async getMultiplePublicKeys(userIds) {
    try {
      // Filter out cached keys
      const uncachedUserIds = userIds.filter(
        (id) => !this.publicKeysCache.has(id)
      );

      if (uncachedUserIds.length === 0) {
        // All keys are cached
        const keysMap = {};
        userIds.forEach((id) => {
          keysMap[id] = this.publicKeysCache.get(id);
        });
        return keysMap;
      }

      // Fetch uncached keys
      const keysMap = await encryptionApiService.getPublicKeys(
        uncachedUserIds
      );

      // Cache them
      Object.entries(keysMap).forEach(([userId, publicKey]) => {
        this.publicKeysCache.set(userId, publicKey);
      });

      // Combine with cached keys
      const allKeys = { ...keysMap };
      userIds.forEach((id) => {
        if (this.publicKeysCache.has(id)) {
          allKeys[id] = this.publicKeysCache.get(id);
        }
      });

      return allKeys;
    } catch (error) {
throw error;
    }
  }

  /**
   * Encrypt a message before sending
   * @param {string} plaintext - Message content
   * @param {string} recipientId - Recipient user ID
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Encrypted package
   */
  async encryptMessage(plaintext, recipientId, conversationId) {
    try {
      if (!this.isE2EEEnabled) {
        throw new Error("E2EE is not enabled");
      }

      if (!this.userPassword) {
        throw new Error("User password not available");
      }
// 1. Get my private key
      const myPrivateKey = await e2eeService.loadPrivateKey(this.userPassword);

      // 2. Get recipient's public key
      const recipientPublicKey = await this.getRecipientPublicKey(recipientId);

      // 3. Encrypt message
      const encryptedPackage = await e2eeService.encryptMessage(
        plaintext,
        recipientPublicKey,
        conversationId,
        myPrivateKey
      );
return encryptedPackage;
    } catch (error) {
throw error;
    }
  }

  /**
   * Decrypt a received message
   * @param {Object} encryptedPackage - Encrypted message
   * @param {string} senderId - Sender user ID
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<string>} Decrypted plaintext
   */
  async decryptMessage(encryptedPackage, senderId, conversationId) {
    try {
      if (!this.userPassword) {
        throw new Error("User password not available");
      }
// 1. Get my private key
      const myPrivateKey = await e2eeService.loadPrivateKey(this.userPassword);

      // 2. Get sender's public key
      const senderPublicKey = await this.getRecipientPublicKey(senderId);

      // 3. Decrypt message
      const plaintext = await e2eeService.decryptMessage(
        encryptedPackage,
        senderPublicKey,
        conversationId,
        myPrivateKey
      );
return plaintext;
    } catch (error) {
throw error;
    }
  }

  /**
   * Process received message (decrypt if encrypted)
   * @param {Object} message - Message object from backend
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Processed message with decrypted content
   */
  async processReceivedMessage(message, conversationId) {
    try {
      // Check if message is encrypted
      if (message.isEncrypted && message.encryptedContent) {
        // E2EE message - decrypt it
        if (message.encryptionType === "e2ee") {
          try {
            const decryptedContent = await this.decryptMessage(
              message.encryptedContent,
              message.sender._id || message.sender,
              conversationId
            );

            return {
              ...message,
              content: decryptedContent,
              decrypted: true,
            };
          } catch (decryptError) {
return {
              ...message,
              content: "🔒 [Decryption failed]",
              error: true,
            };
          }
        }

        // Server-side encrypted - backend already decrypted it
        return message;
      }

      // Plain text message
      return message;
    } catch (error) {
return {
        ...message,
        content: "⚠️ [Error processing message]",
        error: true,
      };
    }
  }

  /**
   * Set user password (after login)
   * @param {string} password - User's password
   */
  setPassword(password) {
    this.userPassword = password;
  }

  /**
   * Check if E2EE is enabled
   */
  isEnabled() {
    return this.isE2EEEnabled;
  }

  /**
   * Enable E2EE
   */
  enable() {
    this.isE2EEEnabled = true;
  }

  /**
   * Disable E2EE (fallback to server-side encryption)
   */
  disable() {
    this.isE2EEEnabled = false;
  }

  /**
   * Clear all cached data (on logout)
   */
  clearCache() {
    this.userPassword = null;
    this.publicKeysCache.clear();
    e2eeService.clearCache();
}

  /**
   * Check if user has encryption keys setup
   */
  hasKeys() {
    return e2eeService.hasKeys();
  }

  /**
   * Get user's public key
   */
  getMyPublicKey() {
    return e2eeService.getPublicKey();
  }
}

// Create and export singleton instance
const encryptionManager = new EncryptionManager();
export default encryptionManager;

